import { Input } from "@components/ui/input";
import { FORM_OPTIONS } from "@constants";
import { useAppContext } from "@contexts/AppContext";
import {
	Box,
	Button,
	Checkbox,
	Fab,
	FormControlLabel,
	Grid,
	Paper,
	Typography,
	useMediaQuery,
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { convertToHebrewDate } from "@services/api";
import useCartStore, { SERVICE_TYPES } from "@stores/cartStore";
import useLanguageStore from "@stores/languageStore";
import { parseDate } from "@utils";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const CustomForm = ({
	properties,
	setProperties,
	editItemId,
	showPreview,
	setShowPreview,
}) => {
	const { t } = useTranslation();
	const { language } = useLanguageStore();
	const isRtl = language === "he";
	const addItem = useCartStore((state) => state.addItem);
	const updateItem = useCartStore((state) => state.updateItem);
	const { showSuccess } = useAppContext();
	const navigate = useNavigate();
	const grieveWordsListId = useId();
	const closenessListId = useId();
	const memorialPlacesListId = useId();
	const isDesktop = useMediaQuery("(min-width:768px)");

	const setName = (name) => setProperties((prev) => ({ ...prev, name }));
	const setSuffix = (suffix) => setProperties((prev) => ({ ...prev, suffix }));
	const setBirthDate = (birthdate) =>
		setProperties((prev) => ({ ...prev, birthdate }));
	const setDeathDate = (deathdate) =>
		setProperties((prev) => ({ ...prev, deathdate }));
	const setShowHebrew = (showHebrew) =>
		setProperties((prev) => ({ ...prev, showHebrew }));
	const setGrieveWords = (grieveWords) =>
		setProperties((prev) => ({ ...prev, grieveWords }));
	const setCloseness = (closeness) =>
		setProperties((prev) => ({ ...prev, closeness }));
	const setHasMemorial = (hasMemorial) =>
		setProperties((prev) => ({ ...prev, hasMemorial }));
	const setMemorialDate = (memorialDate) =>
		setProperties((prev) => ({ ...prev, memorialDate }));
	const setMemorialHour = (memorialHour) =>
		setProperties((prev) => ({ ...prev, memorialHour }));
	const setMemorialPlace = (memorialPlace) =>
		setProperties((prev) => ({ ...prev, memorialPlace }));
	const setHasSeven = (hasSeven) =>
		setProperties((prev) => ({ ...prev, hasSeven }));
	const setSevenStartDate = (sevenStartDate) =>
		setProperties((prev) => ({ ...prev, sevenStartDate }));
	const setSevenEndDate = (sevenEndDate) =>
		setProperties((prev) => ({ ...prev, sevenEndDate }));
	const setSevenStartHour = (sevenStartHour) =>
		setProperties((prev) => ({ ...prev, sevenStartHour }));
	const setSevenEndHour = (sevenEndHour) =>
		setProperties((prev) => ({ ...prev, sevenEndHour }));
	const setFamilyLocation = (familyLocation) =>
		setProperties((prev) => ({ ...prev, familyLocation }));
	const setFamilyName = (familyName) =>
		setProperties((prev) => ({ ...prev, familyName }));

	const [isLoadingHebrewDate, setIsLoadingHebrewDate] = useState(false);
	const [hebrewDateError, setHebrewDateError] = useState(null);

	useEffect(() => {
		const fetchHebrewDate = async () => {
			if (properties.showHebrew && properties.deathdate) {
				const dateComponents = parseDate(properties.deathdate);
				if (!dateComponents) {
					setHebrewDateError(t("obituary.invalidDateFormat"));
					return;
				}

				setIsLoadingHebrewDate(true);
				setHebrewDateError(null);

				try {
					const result = await convertToHebrewDate(
						dateComponents.year,
						dateComponents.month,
						dateComponents.day,
					);
					setProperties((prevProperties) => ({
						...prevProperties,
						hebrewDate: result.hebrew,
					}));
				} catch (error) {
					console.error("Failed to convert to Hebrew date:", error);
					setHebrewDateError(t("obituary.hebrewDateError"));
				} finally {
					setIsLoadingHebrewDate(false);
				}
			}
		};

		fetchHebrewDate();
	}, [properties.showHebrew, properties.deathdate, setProperties, t]);

	return (
		<div className="relative flex h-full w-full flex-col flex-1 overflow-y-auto overflow-x-hidden md:flex-[4]">
			{/* Floating Preview Toggle Button - Mobile Only */}
			{!isDesktop && (
				<Fab
					color="primary"
					aria-label="toggle preview"
					onClick={() => setShowPreview(!showPreview)}
					style={{
						position: "fixed",
						bottom: "24px",
						right: isRtl ? "auto" : "24px",
						left: isRtl ? "24px" : "auto",
						zIndex: 2000,
						backgroundColor: "#424242",
						color: "white",
					}}
				>
					{showPreview ? <VisibilityOffIcon /> : <VisibilityIcon />}
				</Fab>
			)}

			{/* Form Content */}
			<div className="flex-1 px-6 py-6 overflow-x-hidden">
				<form
					className="max-w-2xl mx-auto space-y-6"
					onSubmit={(e) => {
						e.preventDefault();
						// Form submission handled by Add to Cart button
					}}
				>
					{/* Deceased Details */}
					<div className="p-4">
						<Typography
							variant="h6"
							component="h2"
							className="mb-4 pb-2 border-b border-stone-200"
						>
							{t("obituary.deceasedDetails.title")}
						</Typography>
						<Grid container spacing={3}>
							<Grid item xs={12} sm={6}>
								<Input
									label={t("obituary.deceasedDetails.fullName")}
									required
									value={properties.name || ""}
									onChange={(e) => setName(e.target.value)}
									dir={isRtl ? "rtl" : "ltr"}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<Input
									as="select"
									label={t("obituary.deceasedDetails.suffix")}
									value={properties.suffix || ""}
									onChange={(e) => setSuffix(e.target.value)}
									dir={isRtl ? "rtl" : "ltr"}
								>
									<option value="">
										{t("common.selectPlaceholder", "Select an option")}
									</option>
									{FORM_OPTIONS.SUFFIX.filter(
										(option) => option.value && option.value.trim() !== "",
									).map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</Input>
							</Grid>
							<Grid item xs={12} sm={6}>
								<Input
									label={t("obituary.deceasedDetails.birthDate")}
									type="date"
									value={properties.birthdate || ""}
									onChange={(e) => setBirthDate(e.target.value)}
									dir={isRtl ? "rtl" : "ltr"}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<Input
									label={t("obituary.deceasedDetails.deathDate")}
									type="date"
									value={properties.deathdate || ""}
									onChange={(e) => setDeathDate(e.target.value)}
									dir={isRtl ? "rtl" : "ltr"}
								/>
							</Grid>
							<Grid item xs={12}>
								<FormControlLabel
									control={
										<Checkbox
											checked={properties.showHebrew || false}
											onChange={(e) => setShowHebrew(e.target.checked)}
										/>
									}
									label={t("obituary.deceasedDetails.showHebrewDate")}
								/>
							</Grid>
							{isLoadingHebrewDate && (
								<Grid item xs={12}>
									<Typography variant="caption" className="text-stone-500">
										{t("obituary.loadingHebrewDate")}
									</Typography>
								</Grid>
							)}
							{hebrewDateError && (
								<Grid item xs={12}>
									<Typography variant="caption" className="text-red-600">
										{hebrewDateError}
									</Typography>
								</Grid>
							)}
						</Grid>
					</div>

					{/* Grief Words */}
					<div className="p-4">
						<Typography
							variant="h6"
							component="h2"
							className="mb-4 pb-2 border-b border-stone-200"
						>
							{t("obituary.griefWords.title")}
						</Typography>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<Input
									label={t("obituary.griefWords.suggestions")}
									value={properties.grieveWords || ""}
									onChange={(e) => setGrieveWords(e.target.value)}
									dir={isRtl ? "rtl" : "ltr"}
									list={grieveWordsListId}
								/>
								<datalist id={grieveWordsListId}>
									{FORM_OPTIONS.GRIEVE_WORDS.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</datalist>
							</Grid>
							<Grid item xs={12}>
								<Input
									label={t("obituary.griefWords.closeness")}
									value={properties.closeness || ""}
									onChange={(e) => setCloseness(e.target.value)}
									dir={isRtl ? "rtl" : "ltr"}
									list={closenessListId}
								/>
								<datalist id={closenessListId}>
									{FORM_OPTIONS.CLOSENESS.filter((option) => option.value).map(
										(option) => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										),
									)}
								</datalist>
							</Grid>
						</Grid>
					</div>

					{/* Funeral */}
						<div className="p-4">
						<div className="flex items-center justify-between mb-4 pb-2">
							<Typography variant="h6" component="h2">
								{t("obituary.funeral.title")}
							</Typography>
							<FormControlLabel
								control={
									<Checkbox
										checked={properties.hasMemorial || false}
										onChange={(e) => setHasMemorial(e.target.checked)}
									/>
								}
								label={t("obituary.funeral.showInObituary")}
							/>
						</div>
						{properties.hasMemorial && (
							<Grid container spacing={3}>
								<Grid item xs={12} sm={6}>
									<Input
										label={t("obituary.funeral.date")}
										type="date"
										value={properties.memorialDate || ""}
										onChange={(e) => setMemorialDate(e.target.value)}
										dir={isRtl ? "rtl" : "ltr"}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<Input
										label={t("obituary.funeral.time")}
										type="time"
										value={properties.memorialHour || ""}
										onChange={(e) => setMemorialHour(e.target.value)}
										dir={isRtl ? "rtl" : "ltr"}
									/>
								</Grid>
								<Grid item xs={12}>
									<Input
										label={t("obituary.funeral.cemetery")}
										value={properties.memorialPlace || ""}
										onChange={(e) => setMemorialPlace(e.target.value)}
										dir={isRtl ? "rtl" : "ltr"}
										list={memorialPlacesListId}
									/>
									<datalist id={memorialPlacesListId}>
										{FORM_OPTIONS.MEMORIAL_PLACES.map((option) => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}
									</datalist>
								</Grid>
							</Grid>
						)}
					</div>

					{/* Shiva */}
					<div className="p-4">
						<div className="flex items-center justify-between mb-4 pb-2">
							<Typography variant="h6" component="h2">
								{t("obituary.shiva.title")}
							</Typography>
							<FormControlLabel
								control={
									<Checkbox
										checked={properties.hasSeven || false}
										onChange={(e) => setHasSeven(e.target.checked)}
									/>
								}
								label={t("obituary.shiva.showInObituary")}
							/>
						</div>
						{properties.hasSeven && (
							<Grid container spacing={3}>
								<Grid item xs={12} sm={6} md={4}>
									<Input
										label={t("obituary.shiva.fromDate")}
										type="date"
										value={properties.sevenStartDate || ""}
										onChange={(e) => setSevenStartDate(e.target.value)}
										dir={isRtl ? "rtl" : "ltr"}
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={4}>
									<Input
										label={t("obituary.shiva.toDate")}
										type="date"
										value={properties.sevenEndDate || ""}
										onChange={(e) => setSevenEndDate(e.target.value)}
										dir={isRtl ? "rtl" : "ltr"}
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={4}>
									<Input
										label={t("obituary.shiva.fromTime")}
										type="time"
										value={properties.sevenStartHour || ""}
										onChange={(e) => setSevenStartHour(e.target.value)}
										dir={isRtl ? "rtl" : "ltr"}
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={4}>
									<Input
										label={t("obituary.shiva.toTime")}
										type="time"
										value={properties.sevenEndHour || ""}
										onChange={(e) => setSevenEndHour(e.target.value)}
										dir={isRtl ? "rtl" : "ltr"}
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={4}>
									<Input
										label={t("obituary.shiva.atFamilyHome")}
										value={properties.familyName || ""}
										onChange={(e) => setFamilyName(e.target.value)}
										dir={isRtl ? "rtl" : "ltr"}
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={4}>
									<Input
										label={t("obituary.shiva.address")}
										value={properties.familyLocation || ""}
										onChange={(e) => setFamilyLocation(e.target.value)}
										dir={isRtl ? "rtl" : "ltr"}
									/>
								</Grid>
							</Grid>
						)}
					</div>

					{/* Add to Cart Button */}
					<Box className="mt-6 flex justify-center">
						<Button
							variant="contained"
							size="large"
							onClick={() => {
								if (!properties.name) {
									return;
								}
								if (editItemId) {
									// Update existing item
									updateItem(editItemId, properties);
									showSuccess(t("obituary.updatedInCart"));
									// Navigate back to cart after a short delay
									setTimeout(() => {
										navigate("/cart");
									}, 1000);
								} else {
									// Add new item
									addItem({
										serviceType: SERVICE_TYPES.OBITUARY,
										data: properties,
									});
									showSuccess(t("obituary.addedToCart"));
								}
							}}
							disabled={!properties.name}
							className="px-8 py-3 normal-case font-semibold bg-stone-700 hover:bg-stone-600"
						>
							{editItemId ? t("obituary.updateCart") : t("obituary.addToCart")}
						</Button>
					</Box>
				</form>
			</div>
		</div>
	);
};

export default CustomForm;
